import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  useTheme
} from '@mui/material';
import {
  CalendarMonth,
  AccessTime,
  Today
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const DateTime: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setDate(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (num: number): string => {
    return num.toString().padStart(2, '0');
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
      <Grid 
        container 
        spacing={1} 
        sx={{ 
          maxWidth: 480,
          margin: '0 auto',
          px: 1
        }}
      >
        {/* Date Section */}
        <Grid item xs={6}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              height: '100%',
              borderRadius: 2,
              bgcolor: 'background.paper', // 使用更干净的白色背景，AWS 风格常见
              // border: '1px solid', // Removed default border
              // borderColor: 'divider', // Removed default border color
              
              display: 'flex',
              flexDirection: 'column',
              maxWidth: 220,
              margin: '0 auto',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarMonth 
                sx={{ 
                  color: theme.palette.primary.main,
                  mr: 1
                }} 
              />
              <Typography 
                variant="body2" 
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {t('dashboard:widgets.dateTime.date')}
              </Typography>
            </Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.primary.main,
                mb: 1
              }}
            >
              {formatTime(date.getMonth() + 1)}/{formatTime(date.getDate())}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <CalendarMonth sx={{ fontSize: '1rem', color: 'text.secondary', mr: 0.5 }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {date.getFullYear()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Today sx={{ fontSize: '1rem', color: 'text.secondary', mr: 0.5 }} />
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {new Intl.DateTimeFormat(i18n.language, { weekday: 'long' }).format(date)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Time Section */}
        <Grid item xs={6}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              height: '100%',
              borderRadius: 2,
              bgcolor: 'background.paper', // 使用更干净的白色背景，AWS 风格常见
              // border: '1px solid', // Removed default border
              // borderColor: 'divider', // Removed default border color
              
              display: 'flex',
              flexDirection: 'column',
              maxWidth: 220,
              margin: '0 auto',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTime 
                sx={{ 
                  color: theme.palette.primary.main,
                  mr: 1
                }} 
              />
              <Typography 
                variant="body2" 
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {t('dashboard:widgets.dateTime.time')}
              </Typography>
            </Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.primary.main,
                mb: 1
              }}
            >
              {formatTime(date.getHours())}:{formatTime(date.getMinutes())}
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ fontWeight: 500, mb: 0.5 }}
            >
              {formatTime(date.getSeconds())}
              <Typography 
                component="span" 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  ml: 0.5,
                  fontWeight: 500
                }}
              >
                {t('dashboard:widgets.dateTime.seconds')}
              </Typography>
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {`GMT${date.getTimezoneOffset() <= 0 ? '+' : '-'}${Math.abs(Math.floor(date.getTimezoneOffset() / 60)).toString().padStart(2, '0')}:${(Math.abs(date.getTimezoneOffset()) % 60).toString().padStart(2, '0')}`}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DateTime;